import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Banner, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type FC, useCallback, useEffect, useRef, useState} from 'react'
import {Finding} from './Finding'
import {FindingsFilterBar} from './FindingsFilterBar'
import {FindingsSummary} from './FindingsSummary'
import {FixFindingDialog} from './FixFindingDialog'
import type {Finding as FindingType} from './types'
import {useFilterFindings} from './use-filter-findings'
import {useFindings} from './use-findings'
import {useIgnoreFindings} from './use-ignore-findings'

interface FindingsProps {
  pullRequestBasePath: string
}

export const Findings: FC<FindingsProps> = props => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  return (
    <ErrorBoundary
      boundaryName="Findings"
      onError={e => setErrorMessage(e.message)}
      fallback={<FindingsError error={errorMessage} />}
    >
      <ConsecutiveCAPI401sProvider>
        <FindingsInner {...props} />
      </ConsecutiveCAPI401sProvider>
    </ErrorBoundary>
  )
}

interface FindingsErrorProps {
  error?: string | null
}

const FindingsError: FC<FindingsErrorProps> = ({error}) => (
  <Blankslate className="border rounded-3" role="alert">
    <Blankslate.Visual>
      <AlertIcon size="medium" className="color-fg-danger" />
    </Blankslate.Visual>
    <Blankslate.Heading>Failed to load findings</Blankslate.Heading>
    {error && <Blankslate.Description>{error}</Blankslate.Description>}
  </Blankslate>
)

const FindingsInner: FC<FindingsProps> = ({pullRequestBasePath}) => {
  const {
    findings,
    ignoredEntityIds,
    freshness,
    mergeReadiness,
    overviewSummary,
    modelsUrl,
    isLoading,
    error,
    isNotFound,
  } = useFindings({
    pullRequestBasePath,
  })

  const {openFindings, ignoredFindings, handleDismiss, handleReopen, actionError} = useIgnoreFindings(
    findings,
    pullRequestBasePath,
    ignoredEntityIds,
  )

  const {displayedFindings, ...filterProps} = useFilterFindings(openFindings, ignoredFindings)

  const [fixFindings, setFixFindings] = useState<FindingType[] | null>(null)

  // handling optimistic updates for fix tasks
  const [fixTaskIds, setFixTaskIds] = useState<Record<string, string>>({})
  const handleTaskCreated = useCallback((taskId: string, _taskUrl: string, findingIds: string[]) => {
    setFixTaskIds(prev => {
      const next = {...prev}
      for (const id of findingIds) next[id] = taskId
      return next
    })
  }, [])

  const hasNonVisibilityFilters = filterProps.selectedSeverities.size > 0 || filterProps.selectedSources.size > 0

  // Announce when findings finish processing (freshness transitions to 'current')
  const previousFreshnessRef = useRef(freshness)
  useEffect(() => {
    if (previousFreshnessRef.current !== 'current' && freshness === 'current') {
      announce('Findings updated')
    }
    previousFreshnessRef.current = freshness
  }, [freshness])

  // Announce when no findings are available (so screen reader users know loading finished)
  useEffect(() => {
    if (isNotFound) {
      announce('No findings available')
    }
  }, [isNotFound])

  if (isLoading) {
    return (
      <div className="d-flex flex-justify-center tmp-p-6" role="status" aria-label="Loading findings">
        <Spinner size="large" />
      </div>
    )
  }

  if (isNotFound) {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>No findings available</Blankslate.Heading>
        <Blankslate.Description>
          Copilot has not detected any findings for this pull request. This could be because the analysis has not
          completed yet, or because there are no findings to report.
        </Blankslate.Description>
      </Blankslate>
    )
  }

  if (error) {
    return <FindingsError error={error} />
  }

  if (freshness === 'pending') {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>Processing findings</Blankslate.Heading>
        <Blankslate.Description>
          Findings are being processed. This page will update automatically
        </Blankslate.Description>
      </Blankslate>
    )
  }

  if (openFindings.length === 0 && ignoredFindings.length === 0) {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>No findings</Blankslate.Heading>
        <Blankslate.Description>There are no findings for this pull request</Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <section aria-labelledby="findings-heading" className="d-flex flex-column width-full">
      <FindingsSummary mergeReadiness={mergeReadiness} overviewSummary={overviewSummary} />
      {freshness === 'stale' && (
        <Banner variant="info" title="Updating findings">
          New findings are being processed. This page will update automatically.
        </Banner>
      )}
      {actionError && (
        <div role="alert">
          <Banner variant="warning" title="Action failed">
            {actionError}
          </Banner>
        </div>
      )}
      <FindingsFilterBar
        openCount={openFindings.length}
        resolvedCount={ignoredFindings.length}
        displayedCount={displayedFindings.length}
        handleFix={() => setFixFindings(displayedFindings)}
        handleReopen={() => handleReopen(displayedFindings)}
        handleDismiss={() => handleDismiss(displayedFindings)}
        {...filterProps}
      />
      <div role="status" className="sr-only">
        {displayedFindings.length === 0
          ? 'No findings displayed'
          : `Showing ${displayedFindings.length} ${displayedFindings.length === 1 ? 'finding' : 'findings'}`}
      </div>
      {displayedFindings.length === 0 && (
        <Blankslate className="border rounded-3">
          <Blankslate.Heading>
            {hasNonVisibilityFilters
              ? 'No findings match your filters'
              : filterProps.selectedVisibility === 'open'
                ? 'All findings resolved'
                : 'No resolved findings'}
          </Blankslate.Heading>
          <Blankslate.Description>
            {hasNonVisibilityFilters
              ? 'Try changing or clearing the filters to see more findings'
              : filterProps.selectedVisibility === 'open'
                ? 'All findings for this pull request have been resolved'
                : 'There are no resolved findings for this pull request'}
          </Blankslate.Description>
        </Blankslate>
      )}
      {displayedFindings.length > 0 && (
        <ul aria-label="Code review findings">
          {displayedFindings.map(finding => (
            <Finding
              key={finding.id}
              finding={finding}
              resolved={filterProps.selectedVisibility === 'resolved'}
              onDismiss={() => handleDismiss([finding])}
              onReopen={() => handleReopen([finding])}
              onFix={() => setFixFindings([finding])}
              taskId={fixTaskIds[finding.id] ?? finding.taskId}
              taskBasePath={pullRequestBasePath.replace(/\/pull\/.*$/, '')}
              feedbackPath={`${pullRequestBasePath}/code_review_findings/feedback`}
            />
          ))}
        </ul>
      )}
      {fixFindings && (
        <FixFindingDialog
          findings={fixFindings}
          basePath={pullRequestBasePath}
          modelsUrl={modelsUrl}
          onClose={() => setFixFindings(null)}
          onTaskCreated={handleTaskCreated}
        />
      )}
    </section>
  )
}
